/**
 * The Master Services Agreement body, moved verbatim from the old MasterServiceAgreement.tsx.
 *
 * Content only — the scrollable container, the scroll-to-bottom gate, the signature block and the
 * submit live in Agreements.tsx. Keeping the contract text in its own module means legal copy
 * changes never touch flow logic, and MSA_VERSION travels with the text it versions.
 *
 * IMPORTANT: bump MSA_VERSION on any substantive change here, and mirror the change in
 * tach-mobile (its copy is a separate, currently abridged, version) and the landing-page PDF.
 */

export const MSA_VERSION = '3.3';

interface MsaContractTextProps {
  businessName?: string;
}

function MsaContractText({ businessName }: MsaContractTextProps) {
  return (
    <div className="space-y-6 text-sm text-gray-800">
      <div className="text-center">
        <h3 className="text-lg font-bold">MASTER SERVICES AGREEMENT</h3>
      </div>
      {/* Prominent Disclaimer */}
      <div className="rounded-md border-2 border-blue-600 bg-blue-50 p-4">
        <h4 className="mb-2 text-center font-bold uppercase text-blue-900">
          IMPORTANT NOTICE
        </h4>
        <p className="text-center font-semibold text-blue-900">
          Signing this Agreement does NOT constitute approval or guarantee
          account activation. Your application remains subject to review and
          approval by the TACH Carrier Support team. TACH reserves the right to
          approve or reject any application for any reason in its sole
          discretion.
        </p>
      </div>

      <p>
        This Master Services Agreement (the &ldquo;Agreement&rdquo;) is entered
        into this{' '}
        <span className="font-semibold">
          {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>{' '}
        (the &ldquo;Effective Date&rdquo;) by and between{' '}
        <span className="font-semibold">
          Trucking Automated Clearing House, LLC
        </span>
        , a limited liability company organized and existing under the laws of
        the State of Georgia, having its principal place of business at 5
        Concourse Pkwy, #3006, Atlanta, GA 30328 (hereinafter referred to as
        &ldquo;TACH&rdquo;) and{' '}
        <span className="font-semibold">{businessName || 'Carrier'}</span>{' '}
        (&ldquo;Carrier&rdquo;). TACH and Carrier are collectively referred to
        herein as the &ldquo;Parties&rdquo; and each individually as a
        &ldquo;Party.&rdquo;
      </p>

      <div>
        <h4 className="mb-2 font-bold uppercase tracking-wide">WITNESSETH:</h4>
        <p className="mb-2">
          WHEREAS, TACH operates as a financial technology platform that
          facilitates payments, manages funds, and connects Carrier with certain
          third-party service providers, and TACH does not itself provide
          transportation, roadside assistance, vehicle rental, or other
          non-financial services to Carrier;
        </p>
        <p className="mb-2">
          WHEREAS, the Parties contemplate that TACH will provide certain
          services for Carrier in the furtherance of Carrier&apos;s business;
          and
        </p>
        <p>
          WHEREAS, the Parties desire that this Agreement should set forth the
          terms and conditions pursuant to which TACH will provide such services
          to Carrier;
        </p>
      </div>

      <p>
        NOW, THEREFORE, in consideration of the mutual terms, conditions,
        promises and covenants set forth herein, the Parties agree as follows:
      </p>

      <div>
        <h4 className="mb-2 font-bold">1. Services</h4>
        <p>
          TACH shall provide certain services to Carrier
          (&ldquo;Services&rdquo;) as identified in the Schedule of Services,
          appended hereto as Schedule 1. The purpose of this Agreement is to
          establish the terms and conditions that will be applicable to any
          Services provided to Carrier by TACH.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">2. Services Addendum</h4>
        <p className="mb-2">
          (a) The Parties agree that each distinct set of Services will be
          described in a schedule attached hereto or agreed to hereafter and
          incorporated herein (each a &ldquo;Services Addendum&rdquo;).
        </p>
        <p className="mb-2">
          (b) Each Services Addendum shall include detailed
          specifications/descriptions for the Services to be performed by TACH
          thereunder.
        </p>
        <p>
          (c) Each Services Addendum shall be subject to the terms and
          conditions of this Agreement. In the event of a conflict between the
          terms and conditions of this Agreement and any Services Addendum, the
          terms and conditions of the Services Addendum shall control for the
          purposes of the Services covered by such Services Addendum.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">3. Obligations of Parties</h4>
        <p className="mb-2 font-semibold">(a) TACH&apos;s Obligations:</p>
        <p className="mb-2 ml-4">
          (i) TACH agrees to complete the Services in accordance with the terms
          and conditions set forth in this Agreement and the applicable Services
          Addendum. TACH shall perform the Services in an efficient and
          professional manner, consistent with industry standards, and the
          Services shall be free from material error.
        </p>
        <p className="mb-2 ml-4">
          (ii) TACH will be fully responsible for paying all its personnel for
          services rendered to Carrier and will be further responsible for any
          and all benefits for such personnel. TACH will report and pay all
          required tax, social security, and other deductions and withholdings;
          and will make all necessary unemployment tax payments.
        </p>
        <p className="mb-4 ml-4">
          (iii) TACH represents and covenants that all its personnel will be
          familiar with and will comply with any and all applicable rules that
          may be set forth in the Services Addendum.
        </p>

        <p className="mb-2 font-semibold">(b) Carrier&apos;s Obligations:</p>
        <p className="mb-2 ml-4">
          (i) Carrier agrees to make all payments to TACH in accordance with the
          terms and conditions set forth in this Agreement and the applicable
          Services Addendum.
        </p>
        <p className="mb-2 ml-4">
          (ii) Carrier will be fully responsible for paying all its personnel
          for services rendered to Carrier in accordance with the terms and
          conditions set forth in this Agreement and the applicable Services
          Addendum and will be further responsible for any and all benefits for
          such personnel. Carrier agrees to report and pay all required tax,
          social security, and other deductions and withholdings; and will make
          all necessary unemployment tax payments with respect to its personnel.
        </p>
        <p className="ml-4">
          (iii) Carrier shall provide TACH with accurate and complete
          information as reasonably requested by TACH in connection with the
          provision of the Services, including without limitation financial,
          operational, and compliance-related information. Carrier acknowledges
          that TACH&apos;s ability to perform the Services is dependent upon
          Carrier&apos;s timely cooperation and the accuracy of information
          provided by Carrier.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">4. Fees to TACH</h4>
        <p className="mb-2">
          <strong>(a) Membership Tiers.</strong>
        </p>
        <p className="mb-2 ml-4">
          TACH offers three membership tiers. Carrier shall select one tier
          during onboarding. The fees and benefits vary by tier as follows:
        </p>
        <ul className="mb-4 ml-8 list-disc">
          <li className="mb-2">
            <strong>Starter Tier:</strong> No monthly membership fee. TACH may
            charge a one-time application fee in the amount, if any, disclosed
            to Carrier when selecting the Starter Tier during onboarding. Any
            such fee will be deducted from Carrier&apos;s deposits until paid in
            full. Starter tier members do not earn cashback rewards.
          </li>
          <li className="mb-2">
            <strong>Core Tier:</strong> Membership fee of one and nineteen
            hundredths percent (1.19%) of each qualifying revenue deposit. No
            application fee. Core Tier members earn two percent (2%) cashback on
            qualifying fuel purchases and one percent (1%) cashback on other
            qualifying business purchases.
          </li>
          <li className="mb-2">
            <strong>Pro Tier:</strong> Membership fee of one and forty-nine
            hundredths percent (1.49%) of each qualifying revenue deposit. No
            application fee. Pro Tier members earn three percent (3%) cashback
            on qualifying fuel purchases and two percent (2%) cashback on other
            qualifying business purchases. Pro Tier includes roadside assistance
            and deposit-free rental access at no additional per-use charge.
          </li>
        </ul>

        <p className="mb-2">
          <strong>(b) Qualifying Revenue Deposits (Core and Pro Tiers).</strong>
        </p>
        <p className="mb-4 ml-4">
          For Core Tier members (1.19%) and Pro Tier members (1.49%), the
          membership fee applies only to qualifying revenue deposits, defined as
          payments received from factoring companies, freight brokers, shippers,
          or other business revenue sources. Personal transfers and non-revenue
          deposits are not subject to these fees.
        </p>

        <p className="mb-2">
          <strong>(c) Tier Changes.</strong>
        </p>
        <p className="mb-4 ml-4">
          Carrier may request to change membership tiers at any time. Tier
          changes take effect at the start of Carrier&apos;s next monthly
          billing cycle. If Carrier downgrades from Core or Pro tier to Starter
          tier, any accrued but unpaid cashback from the previous billing cycle
          will be forfeited.
        </p>

        <p className="mb-2">
          <strong>(d) Payment; Netting; Set-Off.</strong>
        </p>
        <p className="mb-4 ml-4">
          Membership fees, application fees, and any other undisputed fees are
          due as services are rendered/completed and may be deducted or netted
          from revenue deposits or debited from Carrier&apos;s TACH business
          account. Carrier authorizes TACH to make such deductions or debits and
          to set off any amounts due to TACH against amounts TACH otherwise owes
          to Carrier.
        </p>

        <p className="mb-2">
          <strong>(e) Per-Use Service Fees.</strong>
        </p>
        <p className="mb-4 ml-4">
          Certain services have per-use fees that vary by tier. Roadside
          assistance: Starter $149/use, Core $99/use, Pro $0 (included).
          Deposit-free rental access: Starter $199/use, Core $129/use, Pro $0
          (included). Per-use fees are charged at the time of service.
        </p>

        <p className="mb-2">
          <strong>(f) 30-Day Money-Back Guarantee (Early Cancellation):</strong>
        </p>
        <p className="mb-4 ml-4">
          Notwithstanding Section 9(c), Carrier may terminate this Agreement
          effective immediately by delivering written notice to TACH at any time
          within thirty (30) calendar days after Activation, expressly stating
          Carrier&apos;s election to invoke this 30-Day Money-Back Guarantee.
          Upon a valid early cancellation, TACH will refund to Carrier an amount
          equal to Qualifying TACH Fees collected during the Early Cancellation
          Window.
        </p>

        <p className="mb-2">
          <strong>(g) Fee Adjustments:</strong>
        </p>
        <p className="ml-4">
          TACH reserves the right to adjust membership fees, per-use service
          fees, and cashback percentages upon not less than thirty (30)
          days&apos; prior written notice to Carrier. If Carrier does not agree
          to a fee adjustment, Carrier may terminate this Agreement in
          accordance with Section 9(c) prior to the effective date of the
          adjustment. Carrier&apos;s continued use of the Services after the
          effective date of any fee adjustment constitutes acceptance of the
          revised fees.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          5. Confidential and Proprietary Information
        </h4>
        <p className="mb-2">
          The Parties agree not to disclose, share, or transfer any trade
          secrets or export-controlled technology or items under this Agreement,
          unless expressly agreed to by both Parties in writing. The Parties
          acknowledge that in the course of performing the Services, the Parties
          will have access to Confidential Information and Trade Secrets
          relating to each Party&apos;s business.
        </p>
        <p className="mb-2">
          All Proprietary Information shall be and remain the sole property of
          that Party and shall not be used by the other Party, except as may be
          required in the course of performing the Services, without the prior
          written consent of the disclosing Party.
        </p>
        <p>
          The obligations of confidentiality set forth in this Section 5 shall
          survive termination or expiration of this Agreement for a period of
          two (2) years.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          6. Non-Solicitation of Employees and Contractors
        </h4>
        <p>
          During the term of this Agreement and for a period of one (1) year
          following the termination of this Agreement for any reason whatsoever,
          neither party nor its employees shall, either directly or indirectly,
          solicit, divert or hire, or attempt to solicit, divert or hire, any
          person employed by or contracted with the other Party.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">7. Representations</h4>
        <p className="mb-2 font-semibold">
          7.1 TACH represents and warrants to Carrier that:
        </p>
        <p className="mb-2 ml-4">
          (a) TACH shall perform the Services in a competent, diligent, and
          workmanlike manner, and has the facilities, supplies, staff and
          experience sufficient to provide the Services efficiently and
          expeditiously;
        </p>
        <p className="mb-2 ml-4">
          (b) The execution, delivery and performance of this Agreement by TACH
          does not, and will not conflict with, or result in breach any term,
          condition, obligation or restriction of any other agreement of TACH
          with any third party;
        </p>
        <p className="mb-4 ml-4">
          (c) TACH has the right and title to provide the Services hereunder,
          and TACH&apos;s performance of the Services shall not infringe upon
          the intellectual property rights of a third party.
        </p>

        <p className="mb-2 font-semibold">
          7.2 Carrier represents and warrants to TACH that:
        </p>
        <p className="mb-2 ml-4">
          (d) Carrier holds all licenses, permits, and operating authority
          required under applicable federal, state, and local law to conduct its
          transportation business;
        </p>
        <p className="mb-2 ml-4">
          (e) All information provided by Carrier to TACH, including but not
          limited to financial information, fleet size, and operating data, is
          true, accurate, and complete in all material respects as of the date
          provided;
        </p>
        <p className="ml-4">
          (f) Carrier shall promptly notify TACH of any material change in its
          financial condition, operating authority, or legal status that could
          reasonably be expected to affect TACH&apos;s provision of the Services
          or TACH&apos;s risk exposure under this Agreement.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          8. Role of TACH; Third-Party Services
        </h4>
        <p className="mb-2">
          <strong>(a) TACH as Financial Technology Platform.</strong> Carrier
          acknowledges and agrees that TACH operates solely as a financial
          technology company and payment facilitator. TACH&apos;s role is
          limited to: (i) managing and facilitating payments and fund
          allocations on behalf of Carrier; (ii) connecting Carrier with certain
          third-party service providers and vendor partners; and (iii) where
          applicable, facilitating financial arrangements (such as deposit
          guarantees) to enable Carrier&apos;s access to third-party services.
          TACH does not provide, and shall not be deemed to provide,
          transportation, roadside assistance, vehicle rental, maintenance, or
          any other non-financial services to Carrier.
        </p>
        <p className="mb-2">
          <strong>(b) Third-Party Services Defined.</strong> &ldquo;Third-Party
          Services&rdquo; means any product, service, or benefit made available
          to Carrier through TACH&apos;s vendor partnerships or platform
          integrations, including without limitation roadside assistance,
          vehicle rental programs, fuel card programs, and any other services
          provided by entities other than TACH. Third-Party Services are
          provided by their respective third-party providers (&ldquo;Third-Party
          Providers&rdquo;) and not by TACH.
        </p>
        <p className="mb-2">
          <strong>(c) Voluntary Use.</strong> Carrier&apos;s use of any
          Third-Party Service is entirely voluntary. TACH does not require
          Carrier to utilize any Third-Party Service as a condition of this
          Agreement or any tier of membership.
        </p>
        <p className="mb-2">
          <strong>(d) Contractual Privity.</strong> When Carrier elects to use a
          Third-Party Service, Carrier enters into a direct contractual
          relationship with the applicable Third-Party Provider. Carrier
          acknowledges that TACH is not a party to any such agreement between
          Carrier and a Third-Party Provider, and that all rights, obligations,
          warranties, and liabilities arising from the use of Third-Party
          Services run between Carrier and the applicable Third-Party Provider.
        </p>
        <p className="mb-2 font-semibold uppercase">
          (e) Disclaimer of Warranties for Third-Party Services. TACH MAKES NO
          REPRESENTATIONS OR WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED,
          STATUTORY, OR OTHERWISE, REGARDING ANY THIRD-PARTY SERVICE, INCLUDING
          WITHOUT LIMITATION ANY WARRANTY OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, QUALITY, RELIABILITY, AVAILABILITY, TIMELINESS, OR
          NON-INFRINGEMENT. TACH DOES NOT GUARANTEE THE PERFORMANCE,
          WORKMANSHIP, SAFETY, OR RESULTS OF ANY THIRD-PARTY SERVICE.
        </p>
        <p className="mb-2">
          <strong>(f) Claims Against Third-Party Providers.</strong> Any claims,
          complaints, disputes, or causes of action arising from or relating to
          a Third-Party Service shall be directed solely to the applicable
          Third-Party Provider. Carrier agrees to look exclusively to the
          Third-Party Provider for any remedy or recovery relating to
          Third-Party Services and waives any claim against TACH arising from or
          relating to the acts, omissions, negligence, or defaults of any
          Third-Party Provider.
        </p>
        <p>
          <strong>
            (g) TACH&apos;s Role in Payment Facilitation for Third-Party
            Services.
          </strong>{' '}
          Where TACH facilitates payment, settlement, deposit guarantees, or
          other financial arrangements in connection with a Third-Party Service,
          such financial facilitation does not make TACH a provider, guarantor
          of performance, joint venturer, or agent of the Third-Party Provider.
          TACH&apos;s sole obligation in connection with such arrangements is
          the financial facilitation itself.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          9. Indemnification and Limitation of Liability
        </h4>
        <p className="mb-2">
          TACH shall defend, hold harmless, and indemnify Carrier from and
          against all third party suits, demands, losses, damages, judgments,
          claims or other liability, to the extent that such Liability arises
          from, or is related to TACH&apos;s breach of this Agreement, or
          grossly negligent or intentionally wrongful acts or omissions by TACH.
        </p>
        <p className="mb-2">
          Carrier shall defend, hold harmless, and indemnify TACH from and
          against all Liability, to the extent that such Liability arises from,
          or is related to Carrier&apos;s material breach of this Agreement, or
          grossly negligent or intentionally wrongful acts or omissions by
          Carrier.
        </p>
        <p className="mb-2">
          Carrier shall further defend, hold harmless, and indemnify TACH from
          and against any and all Liability arising from, or related to: (i)
          Carrier&apos;s use of any Third-Party Service; (ii) any dispute
          between Carrier and a Third-Party Provider; (iii) Carrier&apos;s acts
          or omissions in connection with any vehicle, equipment, or service
          obtained through a Third-Party Provider; or (iv) any claim by a
          Third-Party Provider against TACH arising from Carrier&apos;s default,
          misuse, damage, or failure to comply with the terms of a Third-Party
          Provider agreement.
        </p>
        <p className="mb-2 font-semibold uppercase">
          NEITHER PARTY SHALL BE HELD LIABLE TO THE OTHER FOR INDIRECT,
          INCIDENTAL, CONSEQUENTIAL, SPECIAL, PUNITIVE OR EXEMPLARY DAMAGES
          ARISING IN ANY MANNER FROM THE ACTIVITIES CONTEMPLATED BY THIS
          AGREEMENT, WHETHER UNDER CONTRACT, TORT, OR OTHER CAUSE OF ACTION.
        </p>
        <p className="font-semibold uppercase">
          IN NO EVENT SHALL TACH&apos;S TOTAL AGGREGATE LIABILITY UNDER THIS
          AGREEMENT EXCEED THE TOTAL FEES ACTUALLY PAID BY CARRIER TO TACH
          DURING THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT
          GIVING RISE TO SUCH LIABILITY.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          10. Term; Breach; Suspension of Services; Termination
        </h4>
        <p className="mb-2">
          <strong>(a) Term and Renewal.</strong> The term of this Agreement
          shall commence on the Effective Date and continue for one (1) year
          unless sooner terminated. This Agreement shall automatically renew for
          additional one (1) year renewal terms unless either Party provides
          written notice of intent not to renew at least sixty (60) days prior
          to the expiration of the then-active term.
        </p>
        <p className="mb-2">
          <strong>(b) Breach and Suspension of Services.</strong> In the event
          of a material breach of this Agreement by Carrier, TACH may
          immediately suspend any or all of the Services hereunder until Carrier
          cures any and all such breach.
        </p>
        <p className="mb-2">
          <strong>(c) Discretionary Suspension for Risk.</strong> In addition to
          TACH&apos;s rights under subsection (b) above, TACH may, in its sole
          but commercially reasonable discretion, immediately suspend, limit, or
          discontinue any or all Services (including access to Third-Party
          Services, financial products, and TACH platform features) if TACH
          reasonably believes that: (i) Carrier is not operating in good faith;
          (ii) a material adverse change has occurred in Carrier&apos;s
          financial condition, revenue performance, or creditworthiness; (iii)
          Carrier presents a material risk of non-repayment of any amounts owed
          or potentially owed to TACH or a Third-Party Provider; (iv) Carrier
          has engaged in fraudulent, illegal, or deceptive conduct; or (v)
          continued provision of Services would expose TACH to material
          financial, legal, or reputational risk. TACH shall provide notice to
          Carrier promptly following any such suspension, which notice may be
          provided after the suspension takes effect where TACH determines that
          prior notice would be impracticable or would increase TACH&apos;s risk
          exposure.
        </p>
        <p className="mb-2">
          <strong>(d) Termination by Either Party.</strong> Either Party may
          terminate this Agreement upon sixty (60) days&apos; prior written
          notice to the other Party.
        </p>
        <p className="mb-2">
          <strong>(e) Termination for Cause by TACH.</strong> TACH may terminate
          this Agreement immediately upon written notice to Carrier if: (i)
          Carrier breaches any material term of this Agreement and fails to cure
          such breach within fifteen (15) days after written notice thereof;
          (ii) Carrier becomes insolvent, files for bankruptcy, or has a
          receiver appointed for its assets; (iii) Carrier&apos;s operating
          authority is revoked, suspended, or materially restricted; or (iv) any
          of the circumstances described in Section 10(b-1) persist for more
          than thirty (30) days.
        </p>
        <p className="mb-2">
          <strong>(f) Effect of Termination by Carrier.</strong> If Carrier
          terminates this Agreement, Carrier will retain continued access to
          their TACH-established bank account, including deposit and withdrawal
          functionalities, but will immediately forfeit access to all other TACH
          products, services, and benefits.
        </p>
        <p className="mb-2">
          <strong>(g) Effect of Termination by TACH.</strong> If TACH terminates
          this Agreement due to Carrier&apos;s breach, TACH reserves the right
          to restrict access to TACH bank account services and immediately
          revoke access to all other services.
        </p>
        <p>
          <strong>(h) Survival.</strong> Sections 5 (Confidentiality), 6
          (Non-Solicitation), 8 (Role of TACH; Third-Party Services), 9
          (Indemnification and Limitation of Liability), 15 (Governing Law), 16
          (Jurisdiction and Venue), 17 (Dispute Resolution), and 26
          (Attorney&apos;s Fees) shall survive termination or expiration of this
          Agreement. Carrier&apos;s obligation to pay all fees accrued prior to
          the effective date of termination shall likewise survive.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">11. Independent Contractor</h4>
        <p>
          Carrier and TACH shall at all times be considered to be acting as
          independent, arms-length contracting entities. Nothing in this
          Agreement shall be construed to make Carrier or any of Carrier&apos;s
          representatives an employee, agent, joint employer, joint venturer or
          partner of TACH.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">12. Federal, State, and Local Laws</h4>
        <p>
          Each Party is required to comply with all federal, state, and local
          laws governing that Party&apos;s obligation with respect to employees
          and independent contractors.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">13. Assignment and Delegation</h4>
        <p className="mb-2">
          This Agreement shall inure to the benefit of and be binding upon
          Carrier and TACH and their respective successors and permitted
          assigns. Neither Party may make any assignment of this Agreement
          without the prior written consent of the other.
        </p>
        <p>
          Notwithstanding the foregoing, TACH may assign this Agreement or any
          of its rights or obligations hereunder to any affiliate or successor
          entity without Carrier&apos;s consent, provided that such assignee
          assumes all of TACH&apos;s obligations under this Agreement.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          14. Data Rights, Privacy, and Permitted Data Sharing
        </h4>
        <p className="mb-2">
          (a) Collection and Use of Carrier Data. Carrier acknowledges and
          agrees that TACH may collect, process, store, and use data arising
          from or related to Carrier&apos;s use of the Services, including
          Carrier&apos;s business, operational, financial, transactional,
          transportation, safety, compliance, telematics, and other related data
          (&ldquo;Carrier Data&rdquo;), for the purpose of providing,
          maintaining, improving, administering, and enhancing the Services and
          related Carrier benefits, including: (i) monitoring and assessing
          risk; (ii) compliance with applicable law; and (iii) supporting
          Carrier&apos;s participation in the TACH platform and facilitating
          services, products, tools, and resources designed to improve
          Carrier&apos;s operational efficiency, safety, regulatory compliance,
          profitability, and overall business performance.
        </p>
        <p className="mb-2">
          (b) Permitted Sharing with Authorized Third Parties. Carrier
          acknowledges and agrees that TACH may share applicable Carrier Data
          with third-party service providers, business partners, technology
          providers, financial institutions, insurance providers, vendors, and
          other organizations that are directly engaged with Carrier or that
          provide services, products, or operational support benefiting Carrier
          in the ordinary course of Carrier&apos;s business operations
          (&ldquo;Authorized Third Parties&rdquo;). TACH will share Carrier Data
          with Authorized Third Parties only where such data is reasonably
          necessary to: (i) provide services, products, tools, or resources
          requested by or made available to Carrier; (ii) support Carrier&apos;s
          normal day-to-day business operations; (iii) facilitate operational,
          financial, insurance, compliance, safety, transportation, technology,
          or administrative services; (iv) improve Carrier access to business
          solutions, preferred rates, efficiencies, or other benefits available
          through TACH partnerships; or (v) support the legitimate business
          relationship between Carrier and the Authorized Third Party. Examples
          of Authorized Third Parties may include, but are not limited to,
          financial service providers, insurance providers, fuel and expense
          management providers, transportation technology providers, compliance
          providers, maintenance providers, factoring providers, equipment
          providers, and other vendors or partners that provide services
          directly benefiting Carrier.
        </p>
        <p className="mb-2">
          (c) Data Minimization and Appropriate Use. TACH will make reasonable
          efforts to share only the categories of Carrier Data reasonably
          necessary for an Authorized Third Party to provide its applicable
          services or support. Authorized Third Parties may use Carrier Data
          only for legitimate business purposes related to providing services
          to, or supporting, the Carrier relationship, and may not use Carrier
          Data for unrelated purposes.
        </p>
        <p className="mb-2">
          (d) Third-Party Service Providers. TACH may engage third-party service
          providers to process, store, analyze, transmit, or otherwise manage
          Carrier Data in connection with providing the Services. TACH will use
          commercially reasonable efforts to engage reputable third parties that
          maintain appropriate safeguards designed to protect Carrier Data.
        </p>
        <p className="mb-2">
          (e) Carrier Authorization and Consent. By entering into this Agreement
          and participating in the Services, Carrier expressly authorizes TACH
          to share Carrier Data with Authorized Third Parties as described in
          this Section. Carrier understands that certain TACH services,
          benefits, integrations, and partnership offerings may require the
          exchange of Carrier Data between TACH and such Authorized Third
          Parties. Carrier may request additional information regarding the
          categories of third parties with whom Carrier Data may be shared and
          may contact TACH with questions related to its data practices.
        </p>
        <p className="mb-2">
          (f) Confidentiality and Security. TACH shall maintain reasonable
          administrative, technical, and organizational safeguards intended to
          protect Carrier Data against unauthorized access, disclosure,
          alteration, or misuse. TACH and its Authorized Third Parties will
          handle Carrier Data in accordance with applicable laws, contractual
          obligations, and reasonable industry practices.
        </p>
        <p className="mb-2">
          (g) Aggregated and De-Identified Data. Carrier grants TACH a
          non-exclusive, royalty-free license to create, use, analyze, and share
          aggregated, statistical, or de-identified information derived from
          Carrier Data, provided that such information does not identify Carrier
          or disclose confidential Carrier-specific information. Such
          information may be used for business analytics, benchmarking,
          research, product development, and improving the Services.
        </p>
        <p>
          (h) Survival. The rights and obligations contained in this Section
          shall survive termination or expiration of this Agreement to the
          extent necessary to fulfill applicable legal, contractual, or
          operational obligations.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">15. Governing Law</h4>
        <p>
          This Agreement shall be governed by and construed in accordance with
          the laws of the State of Georgia, without regard to principles of
          conflicts of laws thereof.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">16. Jurisdiction and Venue</h4>
        <p>
          Subject to the provisions of Section 17 (Dispute Resolution), the
          Parties hereby irrevocably consent to submit to the jurisdiction of
          the courts of Fulton County, Georgia for any actions arising out of or
          relating to this Agreement.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">17. Dispute Resolution</h4>
        <p className="mb-2">
          In the event of a dispute or disagreement between the parties arising
          out of or relating in any way to this Agreement, the parties hereby
          agree to submit their claims to binding arbitration in Fulton County,
          Georgia under the then current commercial arbitration rules of the
          American Arbitration Association. A single arbitrator shall preside at
          such proceedings, and his/her decision shall be reasoned, binding,
          final, and enforceable in any court having jurisdiction over either
          party.
        </p>
        <p>
          Notwithstanding the foregoing, either Party may seek temporary or
          preliminary injunctive relief in any court of competent jurisdiction
          to prevent irreparable harm pending arbitration, without the
          requirement of posting a bond.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">18. Notices</h4>
        <p className="mb-2">
          All notices which are required, or which may be given under this
          Agreement shall be in writing and shall be deemed given (a) at the
          time that such notice is actually received, or (b) three (3) days
          after mailing by certified mail, return receipt requested, or one (1)
          day after the date sent in the case of delivery by overnight express
          service.
        </p>
        <p>
          Notices may also be delivered by email to the email address on file
          for each Party, and shall be deemed given on the date sent, provided
          that the sending Party retains reasonable evidence of transmission and
          does not receive a delivery failure notification.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">19. Waiver</h4>
        <p>
          The waiver by either Party of any breach of any provision of this
          Agreement shall not operate or be construed as a waiver of any
          subsequent breach of any provision of this Agreement.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">20. Entire Agreement</h4>
        <p>
          This Agreement, together with the Services Addendum, constitutes the
          sole and entire agreement between Carrier and TACH with respect to the
          subject matter hereof, and supersedes all prior and contemporaneous
          written or oral statements, promises, understandings or agreements
          with respect thereto. The Parties agree and acknowledge that (a) this
          Agreement has not been entered into under time pressure, and that both
          Parties have had an adequate opportunity to review this Agreement with
          counsel, (b) there are no oral conditions or promises that supplement
          or modify the Agreement, and (c) this Paragraph 20 does not constitute
          &ldquo;boilerplate,&rdquo; but rather is a critical, substantive
          provision of this Agreement. This Agreement can only be altered,
          amended, or modified by a written agreement signed by Carrier and
          TACH.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">21. Severability</h4>
        <p>
          The provisions of this Agreement are severable, and if any one or more
          provisions are determined to be illegal or unenforceable, in whole or
          in part, the remaining provisions and any partially enforceable
          provisions will remain binding and enforceable. Should any provision
          or part of any provision be held invalid by a court of law, such
          provision or part thereof will be deemed revised and amended in such
          minimal manner as will, under more limited terms, be valid and legally
          effective and consistent with the original tenor of this Agreement.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">22. Construction</h4>
        <p>
          In this Agreement, where applicable, words used in the singular form
          shall be construed as though they also are used in the plural form,
          and vice versa, and the masculine gender shall include the feminine
          and neuter genders, and vice versa.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">23. Headings</h4>
        <p>
          Headings used in this Agreement are for convenience only and shall not
          be considered in construing or interpreting this Agreement.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">24. Counterparts</h4>
        <p>
          This Agreement and any Services Addendum may be executed in
          counterparts, each of which shall be an original, but all of which
          shall together constitute one document. Delivery of a copy of a
          signature by facsimile transmission or an electronic exchange
          methodology shall constitute a valid and binding execution and
          delivery of this Agreement and any Services Addendum, and such
          electronic copy shall constitute an enforceable original document.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">25. Force Majeure</h4>
        <p className="mb-2">
          If either Party is delayed, hindered or prevented from performing any
          act or thing required hereunder by reason of strikes, lockouts, labor
          troubles, casualties, inability to procure materials, failure of
          utilities, governmental laws or regulations, riots, insurrection, war,
          acts of God, or other causes beyond the reasonable control of the
          non-performing Party, then such party shall not be liable and the
          period of performance of any such act shall be extended for a period
          equivalent to the period of such delay.
        </p>
        <p>
          For the avoidance of doubt, force majeure events shall include
          epidemics, pandemics, cyberattacks, failures of third-party technology
          infrastructure, and government-imposed restrictions on travel or
          commerce.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">26. Attorney&apos;s Fees</h4>
        <p>
          Should either Party institute any action or proceeding in any court to
          enforce any of the provisions hereof, for damages by reason of any
          alleged breach of any provision of this Agreement or for the enforcing
          of any covenant herein contained, the prevailing party shall be
          entitled to recover its attorneys&apos; fees from the losing party.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          27. Authorized Transactions; Revenue Monitoring
        </h4>
        <p className="mb-2">
          Carrier hereby agrees to authorize and permit TACH to monitor the flow
          of funds into and out of Carrier&apos;s TACH-provided bank account, in
          order to permit TACH to make recommendations to Carrier as to the
          allocation of such funds and the payment of Carrier&apos;s financial
          obligations.
        </p>
        <p>
          TACH reserves the right to monitor Carrier&apos;s revenue performance
          and assess Carrier&apos;s financial stability. In the event of a
          material adverse change in Carrier&apos;s revenue, TACH may, at its
          sole discretion, reduce or suspend Carrier&apos;s eligibility for
          certain financial services.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          28. Financial Data Sharing Consent for Invoice Factoring (Corporate
          Billing)
        </h4>
        <p className="mb-2">
          Carrier authorizes and instructs TACH to disclose to Corporate
          Billing, LLC certain Derived Financial Data and Invoice Details for
          the purpose of evaluating Carrier for invoice factoring services,
          setting Internal Factoring Limits, and servicing purchased invoices.
        </p>
        <p>
          Carrier may revoke this consent prospectively by written notice to
          TACH. Revocation will not affect disclosures already made or Corporate
          Billing&apos;s evaluation of previously submitted invoices.
        </p>
      </div>

      <div>
        <h4 className="mb-2 font-bold">29. Electronic Acceptance</h4>
        <p>
          Carrier acknowledges that this Agreement may be presented and accepted
          electronically through TACH&apos;s onboarding platform. Carrier&apos;s
          electronic acceptance (including clicking &ldquo;I Agree,&rdquo;
          providing a digital signature, or completing the onboarding process
          after being presented with this Agreement) shall constitute
          Carrier&apos;s binding agreement to all terms and conditions hereof,
          with the same legal force and effect as a handwritten signature on a
          physical document. Carrier represents that the individual accepting
          this Agreement on behalf of Carrier has the authority to bind Carrier.
        </p>
      </div>

      <div className="mt-6">
        <p className="text-center font-bold">
          IN WITNESS WHEREOF, the Parties have caused this Master Services
          Agreement to be duly executed and delivered as of the date first
          written above.
        </p>
      </div>
    </div>
  );
}

export default MsaContractText;
